// PPT utilities for generating outline and managing presentation content
import linkupService from '../../services/linkupService'
import { MODEL_MODES } from '../../constants/agentConfig'

// Generate PPT outline based on query/topic using LLM
export const generatePPTOutline = async (topic, llmService) => {
  let academicContext = ''
  let academicSources = []
  let relevantImages = []
  
  try {
    // SATU REQUEST TUNGGAL untuk mendapatkan semua data yang diperlukan
    console.log(`🔍 Mencari konteks dan gambar untuk topik presentasi: ${topic}`);
    
    // Gunakan search dengan query yang lebih komprehensif untuk mendapatkan sources dan images sekaligus
    const searchResults = await linkupService.search(
      `${topic} presentation data statistics charts infographics visual elements`, 
      'web', 
      'searchResults', 
      'International'
    );
    
    if (searchResults) {
      // Ambil sources untuk konteks
      if (searchResults.sources && searchResults.sources.length > 0) {
        academicSources = searchResults.sources.slice(0, 20); // Simpan 20 sumber untuk digunakan dalam presentasi
        const topSources = academicSources.slice(0, 5);
        academicContext = `\n\nKonteks Referensi:\n${topSources.map((source, index) => 
          `${index + 1}. ${source.title}\n   ${source.snippet}`
        ).join('\n')}`;
        console.log(`✅ Ditemukan ${academicSources.length} sumber untuk outline presentasi`);
      }
      
      // Ambil images untuk seluruh presentasi
      if (searchResults.images && searchResults.images.length > 0) {
        relevantImages = searchResults.images.slice(0, 10).map(img => ({
          url: img.url,
          title: img.title || img.alt || 'Relevant Image',
          description: img.description || img.caption || 'Supporting visual content'
        }));
        console.log(`✅ Ditemukan ${relevantImages.length} gambar untuk presentasi`);
      }
    }
  } catch (error) {
    console.warn('❌ Error saat mencari konteks dan gambar:', error.message);
  }
  
  const prompt = `Buatkan OUTLINE (struktur slide-slide) presentasi PowerPoint untuk topik "${topic}" dalam format JSON dengan struktur berikut:
  {
    "slides": [
      {
        "id": "slide_1",
        "title": "Judul Slide",
        "description": "Deskripsi singkat isi slide",
        "slideType": "title",
        "duration": 2,
        "isEditable": true,
        "visualElements": ["title", "subtitle"]
      }
    ]
  }
  
  PENTING: Ini adalah OUTLINE (kerangka/struktur), BUKAN konten lengkap presentasi!
  
  WAJIB SERTAKAN SLIDE-SLIDE BERIKUT (TIDAK BOLEH DIHILANGKAN):
  1. Title Slide (pembuka)
  2. Agenda/Outline
  3. Konten Utama (bisa dibagi menjadi beberapa slide)
  4. Kesimpulan
  5. Q&A
  6. Thank You/Referensi
  
  Tipe Slide yang Tersedia:
  - "title": Slide judul/pembuka
  - "agenda": Slide agenda/daftar isi
  - "content": Slide konten utama
  - "comparison": Slide perbandingan
  - "process": Slide proses/langkah-langkah
  - "data": Slide dengan data/statistik
  - "conclusion": Slide kesimpulan
  - "qna": Slide Q&A
  - "thankyou": Slide penutup
  
  Visual Elements yang Tersedia:
  - "title", "subtitle", "bullet_points", "numbered_list"
  - "chart", "graph", "diagram", "infographic"
  - "image", "icon", "table", "timeline"
  
  Pedoman:
  - Ikuti struktur presentasi yang engaging dan mudah dipahami
  - Berikan ID unik untuk setiap slide (contoh: slide_1, slide_2, dst)
  - Duration dalam menit (estimasi waktu presentasi per slide)
  - TARGET TOTAL SLIDE: 8-15 SLIDE (sesuaikan dengan kompleksitas topik)
  - Sesuaikan dengan topik: "${topic}"
  - PASTIKAN slide Thank You/Referensi SELALU ada di akhir
  - Pertimbangkan konteks yang relevan jika tersedia${academicContext}
  
  Berikan HANYA JSON tanpa penjelasan tambahan:`
  
  try {
    // Use GENIUS mode for comprehensive presentation planning
    const response = await llmService.sendMessage(
      [{ role: 'user', content: prompt }],
      { 
        mode: MODEL_MODES.GENIUS,
        searchType: 'web',
        stream: false,
        consumeCredits: false
      }
    )
    const cleanedResponse = response.replace(/```json|```/g, '').trim()
    const outline = JSON.parse(cleanedResponse)
    
    if (outline && outline.slides && Array.isArray(outline.slides)) {
      // Pastikan setiap slide memiliki ID dan struktur yang benar
      let processedSlides = outline.slides.map((slide, index) => ({
        id: slide.id || `slide_${index + 1}`,
        title: slide.title,
        description: slide.description || '',
        slideType: slide.slideType || 'content',
        duration: slide.duration || 2,
        isEditable: slide.isEditable !== false,
        visualElements: slide.visualElements || ['bullet_points'],
        topic: topic
      }))
      
      // Validasi dan optimasi jumlah slide
      processedSlides = optimizeSlideCount(processedSlides)
      
      // Validasi dan tambahkan slide wajib jika tidak ada
      const requiredSlides = [
        {
          id: 'slide_thankyou',
          title: 'Terima Kasih & Referensi',
          slideType: 'thankyou',
          duration: 1,
          description: 'Slide penutup dengan ucapan terima kasih dan daftar referensi',
          isEditable: false,
          visualElements: ['title', 'bullet_points']
        }
      ]
      
      // Periksa dan tambahkan slide yang hilang
      requiredSlides.forEach(requiredSlide => {
        const exists = processedSlides.some(slide => slide.slideType === requiredSlide.slideType)
        if (!exists) {
          console.log(`⚠️ Menambahkan slide yang hilang: ${requiredSlide.title}`)
          processedSlides.push({
            ...requiredSlide,
            topic: topic
          })
        }
      })
      
      return {
        outline: processedSlides,
        academicSources: academicSources,
        relevantImages: relevantImages
      }
    } else {
      throw new Error('Format outline tidak valid')
    }
  } catch (error) {
    console.warn('❌ Error generating outline with LLM, using fallback:', error.message);
    // Fallback ke outline default jika LLM gagal
    return {
      outline: getDefaultOutline(topic),
      academicSources: academicSources,
      relevantImages: relevantImages
    }
  }
}

// Fallback outline function dengan struktur presentasi standar
const getDefaultOutline = (topic) => {
  const defaultSlides = [
    {
      id: 'slide_1',
      title: `${topic}`,
      slideType: 'title',
      duration: 2,
      description: 'Slide pembuka presentasi',
      isEditable: true,
      visualElements: ['title', 'subtitle'],
      topic: topic
    },
    {
      id: 'slide_2',
      title: 'Agenda',
      slideType: 'agenda',
      duration: 1,
      description: 'Daftar topik yang akan dibahas',
      isEditable: true,
      visualElements: ['title', 'numbered_list'],
      topic: topic
    },
    {
      id: 'slide_3',
      title: 'Pendahuluan',
      slideType: 'content',
      duration: 3,
      description: 'Pengenalan topik dan latar belakang',
      isEditable: true,
      visualElements: ['title', 'bullet_points', 'image'],
      topic: topic
    },
    {
      id: 'slide_4',
      title: 'Pembahasan Utama',
      slideType: 'content',
      duration: 5,
      description: 'Konten utama presentasi',
      isEditable: true,
      visualElements: ['title', 'bullet_points', 'chart'],
      topic: topic
    },
    {
      id: 'slide_5',
      title: 'Data dan Analisis',
      slideType: 'data',
      duration: 4,
      description: 'Presentasi data dan analisis terkait topik',
      isEditable: true,
      visualElements: ['title', 'chart', 'graph', 'table'],
      topic: topic
    },
    {
      id: 'slide_6',
      title: 'Kesimpulan',
      slideType: 'conclusion',
      duration: 2,
      description: 'Ringkasan dan poin-poin penting',
      isEditable: true,
      visualElements: ['title', 'bullet_points'],
      topic: topic
    },
    {
      id: 'slide_7',
      title: 'Q&A',
      slideType: 'qna',
      duration: 5,
      description: 'Sesi tanya jawab',
      isEditable: true,
      visualElements: ['title', 'icon'],
      topic: topic
    },
    {
      id: 'slide_8',
      title: 'Terima Kasih & Referensi',
      slideType: 'thankyou',
      duration: 1,
      description: 'Slide penutup dengan ucapan terima kasih dan daftar referensi',
      isEditable: false,
      visualElements: ['title', 'bullet_points'],
      topic: topic
    }
  ]
  
  return defaultSlides
}

// Optimize slide count to ensure optimal presentation length
const optimizeSlideCount = (slides) => {
  const MIN_SLIDES = 6
  const MAX_SLIDES = 15
  
  // Jika sudah dalam rentang optimal, kembalikan apa adanya
  if (slides.length >= MIN_SLIDES && slides.length <= MAX_SLIDES) {
    console.log(`✅ Jumlah slide sudah optimal: ${slides.length} slide`)
    return slides
  }
  
  // Jika terlalu sedikit, tambahkan slide konten
  if (slides.length < MIN_SLIDES) {
    console.log(`⚠️ Jumlah slide terlalu sedikit (${slides.length}), menambahkan slide konten`)
    const additionalSlides = MIN_SLIDES - slides.length
    
    for (let i = 0; i < additionalSlides; i++) {
      slides.splice(-1, 0, { // Insert before last slide
        id: `slide_additional_${i + 1}`,
        title: `Pembahasan Tambahan ${i + 1}`,
        slideType: 'content',
        duration: 3,
        description: 'Konten tambahan untuk melengkapi presentasi',
        isEditable: true,
        visualElements: ['title', 'bullet_points'],
        topic: slides[0]?.topic || 'Unknown'
      })
    }
  }
  
  // Jika terlalu banyak, gabungkan atau hapus slide yang kurang penting
  if (slides.length > MAX_SLIDES) {
    console.log(`⚠️ Jumlah slide terlalu banyak (${slides.length}), mengoptimalkan`)
    
    // Prioritas slide yang harus dipertahankan
    const priorityTypes = ['title', 'agenda', 'conclusion', 'qna', 'thankyou']
    const prioritySlides = slides.filter(slide => priorityTypes.includes(slide.slideType))
    const contentSlides = slides.filter(slide => !priorityTypes.includes(slide.slideType))
    
    // Batasi slide konten
    const maxContentSlides = MAX_SLIDES - prioritySlides.length
    const optimizedContentSlides = contentSlides.slice(0, maxContentSlides)
    
    slides = [...prioritySlides, ...optimizedContentSlides].sort((a, b) => {
      const aIndex = parseInt(a.id.replace(/\D/g, '')) || 0
      const bIndex = parseInt(b.id.replace(/\D/g, '')) || 0
      return aIndex - bIndex
    })
  }
  
  return slides
}

// Count total presentation duration
export const calculateTotalDuration = (slides) => {
  return slides.reduce((total, slide) => total + (slide.duration || 0), 0)
}

// Get slide type description
export const getSlideTypeDescription = (slideType) => {
  const descriptions = {
    title: 'Slide pembuka dengan judul presentasi',
    agenda: 'Daftar topik yang akan dibahas',
    content: 'Slide konten utama',
    comparison: 'Slide perbandingan data atau konsep',
    process: 'Slide proses atau langkah-langkah',
    data: 'Slide dengan data, statistik, atau grafik',
    conclusion: 'Slide kesimpulan dan ringkasan',
    qna: 'Slide untuk sesi tanya jawab',
    thankyou: 'Slide penutup dengan ucapan terima kasih'
  }
  
  return descriptions[slideType] || 'Slide konten'
}

// Validate visual elements for slide type
export const validateVisualElements = (slideType, visualElements) => {
  const allowedElements = {
    title: ['title', 'subtitle', 'image', 'icon'],
    agenda: ['title', 'numbered_list', 'bullet_points'],
    content: ['title', 'bullet_points', 'image', 'chart', 'diagram'],
    comparison: ['title', 'table', 'chart', 'diagram'],
    process: ['title', 'numbered_list', 'timeline', 'diagram'],
    data: ['title', 'chart', 'graph', 'table', 'infographic'],
    conclusion: ['title', 'bullet_points', 'icon'],
    qna: ['title', 'icon', 'image'],
    thankyou: ['title', 'bullet_points', 'icon']
  }
  
  const allowed = allowedElements[slideType] || ['title', 'bullet_points']
  return visualElements.filter(element => allowed.includes(element))
}

// Generate slide content suggestions based on type
export const generateSlideContentSuggestions = (slideType, topic) => {
  const suggestions = {
    title: {
      mainPoints: [`Presentasi: ${topic}`, 'Nama Presenter', 'Tanggal Presentasi'],
      details: 'Slide pembuka yang menarik dengan judul yang jelas'
    },
    agenda: {
      mainPoints: ['Pendahuluan', 'Pembahasan Utama', 'Analisis', 'Kesimpulan'],
      details: 'Daftar topik yang akan dibahas dalam presentasi'
    },
    content: {
      mainPoints: ['Poin utama 1', 'Poin utama 2', 'Poin utama 3'],
      details: 'Konten utama dengan penjelasan yang detail dan mudah dipahami'
    },
    data: {
      mainPoints: ['Data terkini', 'Tren dan pola', 'Insight penting'],
      details: 'Presentasi data dengan visualisasi yang menarik dan informatif'
    },
    conclusion: {
      mainPoints: ['Ringkasan poin utama', 'Rekomendasi', 'Langkah selanjutnya'],
      details: 'Kesimpulan yang kuat dengan call-to-action yang jelas'
    }
  }
  
  return suggestions[slideType] || suggestions.content
}